/**
 * Shared competition registration rules — imported by BOTH the frontend
 * (`@shared/competitions`) and the server-side validator
 * (`amplify/functions/submit-event-pre-registration/rules.ts`), following the
 * same single-source-of-truth pattern as `shared/fees.ts`.
 *
 * Keeping the window arithmetic here means `CompetitionsList.tsx`'s button
 * state and the Lambda's rejection can never disagree about when entries open.
 */

import { COMPETITION_DEFAULTS } from './competitionRules';

/**
 * Competition pre-registration opens only within this many hours of the event
 * start.
 *
 * Rationale (club decision, 28 Jul 2026): entries taken far in advance have to
 * be refunded when a competition is cancelled — most often for weather, which
 * is only knowable close to the day. Holding entries to a short window before
 * the event keeps refund exposure small.
 *
 * This applies to COMPETITION registration only. Membership registration and
 * membership payment are deliberately unrestricted and must remain available
 * at any time.
 */
export const PRE_REGISTRATION_WINDOW_HOURS = 24;

/**
 * Returns the instant competition pre-registration opens — exactly
 * PRE_REGISTRATION_WINDOW_HOURS before the event start — or null when
 * `eventDate` is absent or unparseable.
 *
 * Callers must treat null as NOT open (fail closed), so a malformed event
 * record can never accidentally permit unlimited-advance entries.
 */
export function getPreRegistrationOpensAt(eventDate: unknown): Date | null {
  if (typeof eventDate !== 'string' || !eventDate.trim()) return null;
  const start = new Date(eventDate);
  if (Number.isNaN(start.getTime())) return null;
  return new Date(start.getTime() - PRE_REGISTRATION_WINDOW_HOURS * 60 * 60 * 1000);
}

/**
 * Returns the instant competition pre-registration (online sign-on) closes —
 * or null when `eventDate` is absent or unparseable.
 *
 * This is the event's own sign-on cutoff (`registrationCloseTime`) when one
 * is set; otherwise it falls back to the canonical rule of fishing start +
 * `COMPETITION_DEFAULTS.signOnClosesAfterMinutes` (currently 2 hours, i.e.
 * 07:00 -> 09:00). It is deliberately NOT the event start itself.
 *
 * This used to return `eventDate` directly — closing online sign-on the
 * instant fishing began, while the physical sign-on desk stayed open for a
 * further two hours (07:00-09:00). Members trying to sign on online in that
 * window were refused and had to queue at the desk; walk-up check-in
 * (`useActiveCompetition`) correctly honoured the 09:00 cutoff the whole
 * time, so which door a member used decided whether they got in. See
 * `.kiro/specs/competition-timing-canonical/requirements.md` R3. The
 * 07:00-09:00 window is the sign-on window, not a separate membership-only
 * payment desk — do not narrow the cutoff back to `eventDate`.
 */
export function getPreRegistrationClosesAt(
  eventDate: unknown,
  registrationCloseTime?: unknown,
): Date | null {
  if (typeof registrationCloseTime === 'string' && registrationCloseTime.trim()) {
    const explicit = new Date(registrationCloseTime);
    if (!Number.isNaN(explicit.getTime())) return explicit;
  }

  if (typeof eventDate !== 'string' || !eventDate.trim()) return null;
  const start = new Date(eventDate);
  if (Number.isNaN(start.getTime())) return null;
  return new Date(
    start.getTime() + COMPETITION_DEFAULTS.signOnClosesAfterMinutes * 60 * 1000,
  );
}

/**
 * The full eligibility verdict for the timing window:
 *   'NOT_YET_OPEN' — earlier than 24h before the start
 *   'OPEN'         — inside [start - 24h, sign-on close)
 *   'CLOSED'       — at or after sign-on close
 *   'NO_DATE'      — eventDate absent/unparseable; callers must fail closed
 *
 * The opening boundary is inclusive and the closing boundary is exclusive.
 * The window is 24h + signOnClosesAfterMinutes long when using the default
 * cutoff (26h for the standard club rule), or however long the event's own
 * `registrationCloseTime` makes it when one is set.
 */
export type PreRegistrationWindowState = 'NOT_YET_OPEN' | 'OPEN' | 'CLOSED' | 'NO_DATE';

export function getPreRegistrationWindowState(
  eventDate: unknown,
  now: Date,
  registrationCloseTime?: unknown,
): PreRegistrationWindowState {
  const opensAt = getPreRegistrationOpensAt(eventDate);
  const closesAt = getPreRegistrationClosesAt(eventDate, registrationCloseTime);
  if (!opensAt || !closesAt) return 'NO_DATE';
  if (now < opensAt) return 'NOT_YET_OPEN';
  if (now >= closesAt) return 'CLOSED';
  return 'OPEN';
}
